import { useState, useEffect, type FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi, useMutation } from '../hooks/useApi';
import type { ShopProfile, PlatformType } from '../types';
import './ProfilePage.css';

const PLATFORM_NAMES: Record<PlatformType, string> = {
  cafe24: '카페24',
  imweb: '아임웹',
  nhn: 'NHN 고도몰/샵바이',
};

const PLATFORM_ICONS: Record<PlatformType, string> = {
  cafe24: '🛒',
  imweb: '🌐',
  nhn: '🏪',
};

export default function ProfilePage() {
  const { shopId } = useParams<{ shopId: string }>();
  const navigate = useNavigate();

  const { data: shop, isLoading } = useApi<ShopProfile>(
    shopId ? `/shops/${shopId}` : '',
    { immediate: !!shopId }
  );

  const { mutate: updateShop, isLoading: saving, error: saveError } =
    useMutation<ShopProfile, Partial<ShopProfile>>(`/shops/${shopId ?? ''}`, 'PUT');

  const { mutate: deleteShop, isLoading: deleting } =
    useMutation<void>(`/shops/${shopId ?? ''}`, 'DELETE');

  // 폼 상태
  const [mallName, setMallName] = useState('');
  const [description, setDescription] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // shop 데이터가 로드되면 폼 초기화
  useEffect(() => {
    if (shop) {
      setMallName(shop.mallName);
      setDescription(shop.description ?? '');
    }
  }, [shop]);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setSuccessMsg('');

    const result = await updateShop({
      mallName: mallName.trim(),
      description: description.trim(),
    });

    if (result) {
      setSuccessMsg('프로필이 저장되었습니다.');
      setTimeout(() => setSuccessMsg(''), 3000);
    }
  };

  const handleDelete = async () => {
    await deleteShop();
    navigate('/', { replace: true });
  };

  if (!shopId) {
    return (
      <div className="profile-page__error">
        <p>잘못된 접근입니다.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="profile-page">
        <div className="profile-skeleton">
          {[1, 2, 3].map((i) => (
            <div key={i} className="profile-skeleton__block" />
          ))}
        </div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="profile-page__error">
        <p>쇼핑몰 정보를 불러올 수 없습니다.</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* 헤더 */}
      <div className="profile-page__header">
        <div className="profile-page__shop-id">
          <span className="profile-page__shop-icon">
            {PLATFORM_ICONS[shop.platform]}
          </span>
          <div>
            <h2 className="profile-page__title">{shop.mallName}</h2>
            <p className="profile-page__platform">
              {PLATFORM_NAMES[shop.platform]} · {shop.mallId}
            </p>
          </div>
        </div>
        <span
          className={`badge ${
            shop.status === 'active' ? 'badge-success' : 'badge-warning'
          }`}
        >
          {shop.status === 'active' ? '연결 중' : shop.status === 'pending' ? '연결 대기' : '연결 해제'}
        </span>
      </div>

      {/* 기본 정보 폼 */}
      <div className="card profile-page__section">
        <h3 className="card-title">쇼핑몰 정보</h3>
        <form onSubmit={(e) => void handleSave(e)} className="profile-page__form">
          <div className="form-group">
            <label className="form-label" htmlFor="mallName">
              쇼핑몰 이름 <span className="required">*</span>
            </label>
            <input
              id="mallName"
              type="text"
              className="form-input"
              value={mallName}
              onChange={(e) => setMallName(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="description">쇼핑몰 소개</label>
            <textarea
              id="description"
              className="form-input"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="주요 상품 카테고리, 타겟 고객층, 운영 방침 등을 입력해주세요."
            />
            <span className="form-hint">
              AI 팀원들이 쇼핑몰에 맞는 응대와 운영을 하는 데 활용됩니다.
            </span>
          </div>

          {saveError && <div className="alert alert-error">{saveError}</div>}
          {successMsg && <div className="alert alert-success">{successMsg}</div>}

          <div className="profile-page__form-footer">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || !mallName.trim()}
            >
              {saving ? <span className="spinner" /> : '저장하기'}
            </button>
          </div>
        </form>
      </div>

      {/* 연결 정보 */}
      <div className="card profile-page__section">
        <h3 className="card-title">연결 정보</h3>
        <div className="profile-page__connection-info">
          <div className="profile-info-item">
            <span className="profile-info-item__label">플랫폼</span>
            <span className="profile-info-item__value">
              {PLATFORM_ICONS[shop.platform]} {PLATFORM_NAMES[shop.platform]}
            </span>
          </div>
          <div className="profile-info-item">
            <span className="profile-info-item__label">쇼핑몰 ID</span>
            <span className="profile-info-item__value profile-info-item__value--mono">
              {shop.mallId}
            </span>
          </div>
          <div className="profile-info-item">
            <span className="profile-info-item__label">연결 일시</span>
            <span className="profile-info-item__value">
              {new Date(shop.createdAt).toLocaleDateString('ko-KR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          </div>
          <div className="profile-info-item">
            <span className="profile-info-item__label">상태</span>
            <span
              className={`badge ${
                shop.status === 'active' ? 'badge-success' : 'badge-warning'
              }`}
            >
              {shop.status === 'active' ? '정상 연결' : '연결 필요'}
            </span>
          </div>
        </div>

        {shop.status !== 'active' && (
          <button className="btn btn-primary btn-sm" type="button">
            재연결하기
          </button>
        )}
      </div>

      {/* 위험 구역 */}
      <div className="card profile-page__section profile-page__danger-zone">
        <h3 className="card-title profile-page__danger-title">위험 구역</h3>
        <p className="profile-page__danger-desc">
          쇼핑몰 연결을 해제하면 모든 데이터와 AI 팀원 설정이 삭제됩니다.
          이 작업은 되돌릴 수 없습니다.
        </p>

        {showDeleteConfirm ? (
          <div className="profile-page__delete-confirm">
            <p className="alert alert-error">
              정말로 <strong>{shop.mallName}</strong> 연결을 해제하시겠습니까?
            </p>
            <div className="profile-page__delete-btns">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
                type="button"
              >
                취소
              </button>
              <button
                className="btn btn-danger"
                onClick={() => void handleDelete()}
                disabled={deleting}
                type="button"
              >
                {deleting ? <span className="spinner" /> : '연결 해제 확인'}
              </button>
            </div>
          </div>
        ) : (
          <button
            className="btn btn-danger btn-sm"
            onClick={() => setShowDeleteConfirm(true)}
            type="button"
          >
            쇼핑몰 연결 해제
          </button>
        )}
      </div>
    </div>
  );
}
